import * as React from 'react';
import PrimaryLayout from '../../layouts/primary-layout';
import { Button, Card, Col, Form, Input, Row, Typography } from 'antd';
import { useAuth } from '../../stores/use-auth';
import { EditOutlined } from '@ant-design/icons';
import { displayErrorNotifications, displaySuccessNotification } from '../../helpers/toast.helpers';
import { loggingIn } from '../../services/login.services';
import _ from '../../helpers/lodash';
import { useTranslation } from 'react-i18next'; // Import i18next hook

const UserProfile: React.FunctionComponent<any> = () => {
  const [profileDetailsForm] = Form.useForm();
  const [updatePwd, setUpdatePwd] = React.useState(true);
  const { t } = useTranslation(); // Initialize translation hook
  const { tenentName, profileName, userMail, bussinessGroupName, userName } = useAuth(
    ({ tenentName, profileName, userMail, bussinessGroupName, userName }) => ({
      tenentName,
      profileName,
      userMail,
      bussinessGroupName,
      userName
    })
  );

  React.useEffect(() => {
    setUserProfileDetails();
  }, []);

  const setUserProfileDetails = async () => {
    const fName = profileName.split(' ')[0];
    const lName = profileName.split(' ')[1];
    profileDetailsForm.setFieldsValue({
      userName: tenentName,
      firstName: fName,
      lastName: lName,
      email: userMail
    });
  };

  const handleOnPasswordUpdate = async () => {
    const { currentPassword, updatePassword, confirmPassword } = profileDetailsForm.getFieldsValue();
    if (updatePassword !== confirmPassword) {
      displayErrorNotifications([{ message: t('updatePasswordConfirmPasswordMismatch') }]);
    } else {
      const bodyData = {
        realm: bussinessGroupName,
        client_id: useAuth?.getState()?.tenentName,
        current_password: currentPassword,
        password: updatePassword,
        confirm_password: confirmPassword,
        temporary: false,
        username: userName
      };
      const { errors } = await loggingIn.setResetPassword(bodyData);
      if (_.isEmpty(errors)) {
        displaySuccessNotification({ message: t('passwordUpdatedSuccessfully') });
        profileDetailsForm.setFieldsValue({
          currentPassword: '',
          updatePassword: '',
          confirmPassword: ''
        });
        setUpdatePwd(true);
      } else {
        displayErrorNotifications(errors);
      }
    }
  };

  const validateConfirmPassword = (rule: any, value: string) => {
    const { getFieldValue } = profileDetailsForm;
    const updatePassword = getFieldValue('updatePassword');
    if (value && value !== updatePassword) {
      return Promise.reject(t('confirmPasswordMustMatchUpdatePassword'));
    }
    return Promise.resolve();
  };

  return (
    <PrimaryLayout>
      <div className="w-full mx-auto px-4">
        <Card>
          <Form form={profileDetailsForm} layout="vertical" onFinish={handleOnPasswordUpdate}>
            <Row gutter={[5, 10]}>
              <Col xs={26} lg={12}>
                <div className="column-content min-w-90 p-2">
                  <Typography className={`font-bold mb-4`}>{t('profileDetails')}</Typography>
                  <Row gutter={8}>
                    <Col xs={24} lg={12}>
                      <Form.Item label={t('firstName')} name="firstName">
                        <Input size="large" placeholder={t('firstName')} disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item label={t('lastName')} name="lastName">
                        <Input size="large" placeholder={t('lastName')} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24}>
                      <Form.Item label={t('email')} name="email">
                        <Input size="large" placeholder={t('email')} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24}>
                      <Form.Item label={t('userName')} name="userName">
                        <Input size="large" placeholder={t('userName')} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={26} lg={12}>
                <div className="column-content min-w-90 p-2">
                  <div className="flex justify-between">
                    <Typography className={`font-bold mb-4`}>{t('changePassword')}</Typography>
                    <EditOutlined className="cursor-pointer" onClick={() => setUpdatePwd(false)} />
                  </div>
                  <Col xs={24}>
                    <Form.Item
                      label={t('currentPassword')}
                      name="currentPassword"
                      rules={[{ required: true, message: t('currentPasswordCantBeEmpty') }]}
                    >
                      <Input.Password size="large" placeholder={t('currentPassword')} disabled={updatePwd} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t('updatePassword')}
                      name="updatePassword"
                      rules={[
                        { required: true, message: t('updatePasswordCantBeEmpty') },
                        {
                          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                          message: t('passwordPatternMessage')
                        }
                      ]}
                    >
                      <Input.Password size="large" placeholder={t('updatePassword')} disabled={updatePwd} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t('confirmPassword')}
                      name="confirmPassword"
                      rules={[
                        { required: true, message: t('confirmPasswordCantBeEmpty') },
                        { validator: validateConfirmPassword }
                      ]}
                      validateTrigger="onBlur"
                    >
                      <Input.Password size="large" placeholder={t('confirmPassword')} disabled={updatePwd} />
                    </Form.Item>
                  </Col>
                  {!updatePwd && (
                    <div className="flex flex-row-reverse gap-5">
                      <Button type="primary" size="large" htmlType="submit">
                        {t('update')}
                      </Button>
                      <Button
                        size="large"
                        onClick={() => {
                          setUpdatePwd(true);
                          profileDetailsForm.setFieldsValue({
                            currentPassword: '',
                            updatePassword: '',
                            confirmPassword: ''
                          });
                        }}
                      >
                        {t('cancel')}
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>
    </PrimaryLayout>
  );
};

export default UserProfile;
